import React, { PropsWithChildren, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isEqual } from "lodash";
import toast from "react-hot-toast";
import { userPreferences } from "../../actions/actions";
import { putUserPreferences } from "../../services/leadService";
import { PreferenceI } from "./leadsGrid/leads-grid";
import { Columns, Search } from "lucide-react";
import logger, { Logger } from '../../utils/logger';
import { ColumnRegistry } from "../../utils/columnRegistry";
import PopupComponent from "../../components/PopupComponent"; // Import the PopupComponent
import { store } from "../../store/store";

interface IManageLeadsColumns {
  defaultColumns?: { value: string; label: string }[];
  readOnlyColumns?: string[];
}

const ManageLeadsColumns: React.FC<PropsWithChildren<IManageLeadsColumns>> = ({
  defaultColumns = [],
  readOnlyColumns = [],
}) => {
  const dispatch = useDispatch();
// ✅ ENHANCED SELECTOR - Get both user preferences AND custom forms:
const preferences: PreferenceI = useSelector(
  (state: { rootReducers: { user: { userPreferences: any }, userData: { userDetails: any } } }) =>
    state?.rootReducers?.user?.userPreferences
);

const customFormFields = useSelector(
  (state: any) => 
    state?.rootReducers?.userData?.userDetails?.userPreference?.customForm || []
);

// ✅ ADD MEMOIZATION to prevent infinite loops:
const memoizedCustomFormFields = useMemo(() => {
  return customFormFields || [];
}, [JSON.stringify(customFormFields)]); 

  const [columns, setColumns] = useState<{ value: string; label: string; type?: string }[]>([]);
  const [showPopup, setShowPopup] = useState(false);
  const [checkedColumns, setCheckedColumns] = useState<string[]>(
    preferences?.leadGridPreferences?.columnOrders || []
  );
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [filteredColumns, setFilteredColumns] = useState<{ value: string; label: string; type?: string }[]>([]);

  // This effect combines all column sources and deduplicates them
  useEffect(() => {
    // Start with registry columns to ensure all core columns are always available
    const registryColumns = Object.entries(ColumnRegistry).map(([id, def]) => ({
      label: def.headerName,
      value: id,
      type: def.type || 'text' // Extract type from registry if available
    }));
    
    // Process columns passed from props (mostly for backward compatibility)
    const propsColumns = defaultColumns.map(col => ({
      label: col.label || '',
      value: col.value || '',
      type: 'text' // Default type for prop columns
    })).filter(col => col.label && col.value);
    
    // Get custom form fields if available
    const customFormColumns = (preferences?.customForm || []).map(form => ({
      label: form.label,
      value: form.value,
      type: form.type || 'custom' // Extract type from custom form if available
    }));
    
    // Combine all sources with custom form fields taking precedence for labels
    const combinedColumns = [
      ...registryColumns,
      ...propsColumns,
      ...customFormColumns
    ];
    
    // Deduplicate by value (column ID) and keep the last occurrence
    // which gives precedence to custom form fields
    const uniqueColumns = [];
    const seenValues = new Set();
    
    // Process in reverse to give priority to later items
    for (const column of [...combinedColumns].reverse()) {
      if (column && column.value && !seenValues.has(column.value)) {
        seenValues.add(column.value);
        uniqueColumns.push(column);
      }
    }
    
    // Reverse back to original order
    uniqueColumns.reverse();
    
    Logger.log('Processed columns:', uniqueColumns);
    
    setColumns(uniqueColumns);
  }, [preferences.customForm, defaultColumns]);


  
  // Filter columns based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredColumns(columns);
      return;
    }
    
    const searchTermLower = searchTerm.toLowerCase();
    const filtered = columns.filter(
      column => 
        column.label.toLowerCase().includes(searchTermLower) || 
        column.value.toLowerCase().includes(searchTermLower)
    );
    
    setFilteredColumns(filtered);
  }, [searchTerm, columns]);

 // ✅ REPLACE WITH THIS ENHANCED VERSION:
useEffect(() => {
  logger.log('🔧 COLUMN PROCESSING - Starting column combination');
  
  // Start with registry columns to ensure all core columns are always available
  const registryColumns = Object.entries(ColumnRegistry).map(([id, def]) => ({
    label: def.headerName,
    value: id,
    type: def.type || 'text'
  }));
  logger.log('🔧 Registry columns:', registryColumns.length, registryColumns.map(c => c.label));
  
  // Process columns passed from props (mostly for backward compatibility)
  const propsColumns = defaultColumns.map(col => ({
    label: col.label || '',
    value: col.value || '',
    type: 'text'
  })).filter(col => col.label && col.value);
  logger.log('🔧 Props columns:', propsColumns.length, propsColumns.map(c => c.label));
  


const customFormColumns = (memoizedCustomFormFields || [])

  .filter((form: { label: any; value: any; }) => {
    // Filter out invalid entries
    const isValid = form && form.label && form.value;
    if (!isValid) {
      logger.log('🔧 Skipping invalid custom form field:', form);
    }
    return isValid;
  })
  .map((form: { label: any; value: any; type: any; }) => {
   
    return {
      label: form.label,
      value: form.value,
      type: form.type || 'custom'
    };
  });
  logger.log('🔧 Custom form columns processed:', customFormColumns.length, customFormColumns.map((c: { label: any; }) => c.label));
  
  // Combine all sources with custom form fields taking precedence for labels
  const combinedColumns = [
    ...registryColumns,
    ...propsColumns,
    ...customFormColumns
  ];
  
  logger.log('🔧 Combined columns total:', combinedColumns.length);
  
  // Deduplicate by value (column ID) and keep the last occurrence
  // which gives precedence to custom form fields
  const uniqueColumns = [];
  const seenValues = new Set();
  
  // Process in reverse to give priority to later items
  for (const column of [...combinedColumns].reverse()) {
    if (column && column.value && !seenValues.has(column.value)) {
      seenValues.add(column.value);
      uniqueColumns.push(column);
    }
  }
  
  // Reverse back to original order
  uniqueColumns.reverse();
  
  logger.log('🔧 Final unique columns:', uniqueColumns.length, uniqueColumns.map(c => `${c.label} (${c.type})`));
  
  setColumns(uniqueColumns);
}, [memoizedCustomFormFields, defaultColumns]);
 // ✅ Enhanced dependency


// ✅ REPLACE YOUR SECOND useEffect WITH THIS EDGE CASE FIX:
useEffect(() => {
  const storedColumns = preferences?.leadGridPreferences?.columnOrders || [];
  
  // EDGE CASE: Only run migration if user has preferences but missing ALL essential columns
  if (storedColumns.length > 0) {
    const hasEssentialColumns = readOnlyColumns.some(col => storedColumns.includes(col));
    
    // EDGE CASE FIX: Only auto-migrate if user has ZERO essential columns (complete migration needed)
    if (!hasEssentialColumns) {
      logger.log('🔄 EDGE CASE: User has preferences but missing ALL essential columns - auto-migrating');
      
      // Auto-migrate: save current selection + essential columns
      const migratedColumns = [
        ...readOnlyColumns,  // Add essential columns
        ...storedColumns     // Keep their existing selection
      ];
      
      logger.log('🔄 EDGE CASE: Auto-saving migrated columns:', migratedColumns);
      
      // Auto-save the migrated preferences
      const leadGridPreferences = {
        ...preferences.leadGridPreferences,
        columnOrders: migratedColumns,
      };
      
      dispatch(userPreferences({
        ...preferences,
        leadGridPreferences,
      }));
      
      // DON'T set checkedColumns here - let the first useEffect handle UI state
    }
  }
}, [preferences?.leadGridPreferences?.columnOrders, readOnlyColumns, dispatch, preferences]);

// ✅ ADD THIS DEBUGGING CODE FIRST:
useEffect(() => {
  logger.log('🔧 CUSTOM FORM DEBUG - Full preferences object:', preferences);
  logger.log('🔧 CUSTOM FORM DEBUG - customForm path:', preferences?.customForm);
  logger.log('🔧 CUSTOM FORM DEBUG - customForm length:', preferences?.customForm?.length);
  
  if (preferences?.customForm) {
    logger.log('🔧 CUSTOM FORM DEBUG - Custom form fields:', 
      preferences.customForm.map(form => ({
        label: form.label,
        value: form.value,
        type: form.type
      }))
    );
  }
  
  // Check if it's nested elsewhere
  logger.log('🔧 All preference keys:', Object.keys(preferences || {}));
}, [preferences]);
 
  const onApply = () => {
    // Ensure uniqueness to avoid duplicates
    const uniqueCheckedColumns = [...new Set(checkedColumns)];
    
    // Save COMPLETE grid state - all selected columns (including essential ones)
    Logger.log('Storing complete grid state:', uniqueCheckedColumns);
    
    const leadGridPreferences = {
      ...preferences.leadGridPreferences,
      columnOrders: uniqueCheckedColumns,  // Save ALL selected columns
    };
    

    logger.log('🔧 BEFORE DISPATCH:', {
  currentPreferences: preferences,
  newLeadGridPreferences: leadGridPreferences,
  isEqual: isEqual(leadGridPreferences, preferences.leadGridPreferences)
});

    const isEq = isEqual(leadGridPreferences, preferences.leadGridPreferences);
    if (!isEq) {
      dispatch(
        userPreferences({
          ...preferences,
          leadGridPreferences,
        })
      );
      logger.log('🔧 AFTER DISPATCH - checking store');
setTimeout(() => {
  const state = store.getState();
  logger.log('🔧 REDUX STATE CHECK:', {
  
    fullPath: state.rootReducers?.user?.userPreferences
  });
}, 100);
      handleAddGridPreferences(leadGridPreferences);
    }
    
    setShowPopup(false);
  };

  const handleAddGridPreferences = async (data: any) => {
    try {
      const response = await putUserPreferences({ leadGridPreferences: data });
      if (response && response.status) {
        // Success
      }
    } catch (err: any) {
      if (err?.response?.data?.message) {
        toast.error(err?.response?.data?.message);
      } else {
        toast.error("Error while updating column preferences!");
      }
    }
  };

  const handleColumnCheck = (value: string, isChecked: boolean) => {
    Logger.log(`Column ${value} checked: ${isChecked}`);
    
    const updatedColumns = isChecked
      ? [...checkedColumns, value]
      : checkedColumns.filter((column) => column !== value);
    
    setCheckedColumns(updatedColumns);
  };

  // Footer with action buttons
  const popupFooter = (
    <div className="d-flex justify-content-center w-100">
      <button
        className="btn btn-primary px-5 py-2"
        style={{ fontSize: "16px", minWidth: "200px" }}
        onClick={onApply}
      >
        Apply
      </button>
    </div>
  );

  return (
    <div className="position-relative">
      <button
        onClick={() => setShowPopup(true)}
        className="btn d-flex align-items-center"
        style={{
          backgroundColor: "#f1f5f9",
          borderRadius: "6px",
          border: "1px solid #e2e8f0",
          height: "36px",
          fontSize: "14px",
          fontWeight: "500",
          color: "#334155",
          boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
          cursor: "pointer",
          transition: "background-color 0.2s"
        }}
        onMouseOver={(e) => e.currentTarget.style.backgroundColor = "#e2e8f0"}
        onMouseOut={(e) => e.currentTarget.style.backgroundColor = "#f1f5f9"}
        data-tour="edit-columns"
        title="Edit Columns"
        aria-label="Edit Columns"
      >
        <Columns className="me-2" size={16} />
        Edit Columns
      </button>

      <PopupComponent
        isOpen={showPopup}
        onClose={() => setShowPopup(false)}
        title="Manage Columns"
        size="md"
        footer={popupFooter}
      >
        <div className="px-3 py-2 border-bottom">
          <div className="position-relative">
            <input
              type="text"
              className="form-control"
              placeholder="Search columns..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                paddingLeft: "35px",
                height: "38px",
                borderRadius: "4px",
                fontSize: "14px"
              }}
            />
            <Search 
              size={16} 
              className="position-absolute text-muted" 
              style={{ top: "11px", left: "12px" }} 
            />
            {searchTerm && (
              <button
                className="btn btn-sm position-absolute"
                style={{ top: "7px", right: "10px", background: "none", border: "none" }}
                onClick={() => setSearchTerm("")}
              >
                <span>&times;</span>
              </button>
            )}
          </div>
        </div>
        <div style={{ maxHeight: "500px", overflowY: "auto" }}>
          {filteredColumns.length === 0 ? (
            <div className="p-4 text-center text-muted">
              No columns found matching "{searchTerm}"
            </div>
          ) : (
            filteredColumns.map((column) => {
             
              return (
                <div key={column.value} className="px-3 py-3 hover-bg-light border-bottom">
                  <label className="d-flex align-items-center m-0 cursor-pointer user-select-none">
                    <input
                      type="checkbox"
                      className="form-check-input me-3"
                      style={{ width: "20px", height: "20px" }}
                      checked={checkedColumns.includes(column.value)}
                      onChange={(e) =>
                        handleColumnCheck(column.value, e.target.checked)
                      }
                      disabled={readOnlyColumns.includes(column.value)}
                    />
                    <div className="d-flex flex-column">
                      <span className="fs-6 fw-medium">{column.label}</span>
                      <span className="text-muted mt-1">Type: {column.type || 'text'}</span>
                    </div>
                  </label>
                </div>
              );
            })
          )}
        </div>
      </PopupComponent>
    </div>
  );
};

export default ManageLeadsColumns;